<template>
  <div class="jiaoyishuju-page">
    <!-- 顶部导航栏 -->
    <div class="page-topbar">
      <button class="back-btn" @click="handleBack">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
             stroke-linecap="round" stroke-linejoin="round">
          <polyline points="15 18 9 12 15 6" />
        </svg>
        返回配置
      </button>
      <div class="topbar-title">交易数据</div>
    </div>

    <div class="page-content">
      <!-- 加载中 -->
      <div v-if="loading" class="state-view">
        <i class="el-icon-loading" style="font-size:44px;color:#94a3b8;"></i>
        <span>正在加载交易数据...</span>
      </div>

      <!-- 数据展示 -->
      <template v-else-if="chartReady">
        <div class="product-header">
          <h1 class="product-name">{{ productName }}</h1>
          <span class="product-code">{{ productCode }}</span>
        </div>
        <div class="date-section">
          <span class="date-range">数据区间：{{ startDate }} ~ {{ endDate }}</span>
          <div class="date-switch">
            <button class="switch-btn" :class="{ active: dateRange === 'all' }"
                    @click="switchDateRange('all')">全部</button>
            <button class="switch-btn" :class="{ active: dateRange === 'year' }"
                    @click="switchDateRange('year')">近一年</button>
          </div>
        </div>

        <div class="chart-container">
          <div ref="chartRef" class="price-chart"></div>
        </div>

        <div class="metrics-row">
          <div class="metric-card">
            <div class="metric-label">区间最高价</div>
            <div class="metric-value red">{{ formatPrice(metrics.max) }}</div>
          </div>
          <div class="metric-card">
            <div class="metric-label">区间最低价</div>
            <div class="metric-value green">{{ formatPrice(metrics.min) }}</div>
          </div>
          <div class="metric-card">
            <div class="metric-label">区间涨幅</div>
            <div class="metric-value" :class="metrics.changePercent >= 0 ? 'red' : 'green'">
              {{ metrics.changePercent >= 0 ? '+' : '' }}{{ metrics.changePercent.toFixed(2) }}%
            </div>
          </div>
        </div>

        <div class="metrics-formula-section">
          <div class="section-header">
            <div class="section-title">指标计算公式</div>
            <div class="metric-tabs">
              <button class="metric-tab" :class="{ active: activeMetric === 'annualReturn' }"
                      @click="switchMetric('annualReturn')">年化收益率</button>
              <button class="metric-tab" :class="{ active: activeMetric === 'volatility' }"
                      @click="switchMetric('volatility')">年化波动率</button>
              <button class="metric-tab" :class="{ active: activeMetric === 'sharpeRatio' }"
                      @click="switchMetric('sharpeRatio')">夏普比率</button>
              <button class="metric-tab" :class="{ active: activeMetric === 'maxDrawdown' }"
                      @click="switchMetric('maxDrawdown')">最大回撤</button>
            </div>
          </div>
          <div class="metric-content-row">
            <div class="metric-formula-left">
              <div class="formula-header">
                <div class="formula-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                       stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
                    <polyline points="16 7 22 7 22 13"></polyline>
                  </svg>
                </div>
                <div class="formula-title">{{ currentMetricTitle }}</div>
              </div>
              <div class="formula-box">
                <!-- <span class="formula-label">Calculation</span> -->
                <pre class="formula-text">{{ currentFormula }}</pre>
              </div>
              <div class="formula-desc">{{ currentMetricDesc }}</div>
            </div>
            <div class="metric-calc-right">
              <div class="calc-header">计算过程</div>
              <div class="calc-steps">
                <div class="calc-step" v-for="(step, index) in currentCalculationSteps" :key="index">
                  <span class="step-number">{{ index + 1 }}</span>
                  <span class="step-text">{{ step }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- 无数据 -->
      <div v-else-if="!loading" class="state-view">
        <i class="el-icon-document" style="font-size:44px;color:#94a3b8;"></i>
        <span>暂无交易数据</span>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import apiOpen from '@/api/open'

export default {
  name: 'Jiaoyishuju',
  data () {
    return {
      productCode: '',
      productName: '',
      startDate: '',
      endDate: '',
      originalStartDate: '',
      originalEndDate: '',
      dateRange: 'all',
      loading: false,
      chartReady: false,
      chart: null,
      activeMetric: 'annualReturn',
      metrics: {
        max: 0,
        min: 0,
        changePercent: 0
      },
      metricData: {
        annualReturn: null,
        volatility: null,
        sharpeRatio: null,
        maxDrawdown: null
      }
    }
  },
  created () {
    this.productCode = this.$route.query.code || ''
    this.productName = this.$route.query.name || ''
    this.startDate = this.$route.query.start || ''
    this.endDate = this.$route.query.end || ''

    if (!this.productCode) {
      try {
        const cached = JSON.parse(localStorage.getItem('jiaoyishuju_params') || 'null')
        if (cached) {
          this.productCode = cached.code || ''
          this.productName = cached.name || ''
          this.startDate = cached.start || ''
          this.endDate = cached.end || ''
        }
      } catch (e) { /* ignore */ }
    }

    if (!this.startDate || !this.endDate) {
      this.endDate = new Date().toISOString().split('T')[0]
    }

    this.originalStartDate = this.startDate
    this.originalEndDate = this.endDate
  },
  mounted () {
    this.loadData()
    this.loadMetrics()
    window.addEventListener('resize', this.handleResize)
    window.addEventListener('storage', this.handleStorageSync)
  },
  computed: {
    currentMetricData () {
      return this.metricData[this.activeMetric] || {}
    },
    currentMetricTitle () {
      return this.currentMetricData.name || ''
    },
    currentFormula () {
      return this.currentMetricData.formula || ''
    },
    currentMetricDesc () {
      return this.currentMetricData.description || ''
    },
    currentCalculationSteps () {
      return this.currentMetricData.calculationSteps || []
    },
    currentMetricValue () {
      const data = this.currentMetricData
      if (!data || typeof data !== 'object') return '-'
      if (data.value !== undefined && data.value !== null) {
        if (this.activeMetric === 'sharpeRatio') {
          return Number(data.value).toFixed(4)
        }
        return (Number(data.value) * 100).toFixed(2) + '%'
      }
      return '-'
    }
  },
  beforeDestroy () {
    if (this.chart) {
      this.chart.dispose()
      this.chart = null
    }
    window.removeEventListener('resize', this.handleResize)
    window.removeEventListener('storage', this.handleStorageSync)
  },
  methods: {
    loadData () {
      if (!this.productCode) {
        this.$message.error('缺少产品代码')
        return
      }
      this.loading = true
      apiOpen.HistoryPrices({
        code: this.productCode,
        startDate: this.startDate,
        endDate: this.endDate
      }).then(res => {
        console.log('HistoryPrices 返回：', res)
        const prices = Array.isArray(res) ? res : (res?.data || res?.prices || [])
        if (prices && prices.length > 0) {
          const mapped = prices.map(item => ({
            date: item.tradingDate || item.date || '',
            close: item.price ?? item.close ?? item.value ?? 0
          }))
          this.loading = false
          this.chartReady = true
          this.$nextTick(() => {
            this.renderChart(mapped)
          })
          // 数据加载完成后请求指标
          this.loadMetrics()
        } else {
          this.loading = false
        }
      }).catch(err => {
        console.error('HistoryPrices 请求失败：', err)
        this.$message.error('获取交易数据失败')
        this.loading = false
      })
    },

    loadMetrics () {
      if (!this.productCode) {
        this.$message.error('缺少产品代码')
        return
      }
      apiOpen.MetricFormulas({
        code: this.productCode,
        startDate: this.startDate,
        endDate: this.endDate
      }).then(res => {
        console.log('MetricFormulas 返回：', res)
        const data = res?.data || res
        this.metricData.annualReturn = data.annualReturn || null
        this.metricData.volatility = data.volatility || null
        this.metricData.sharpeRatio = data.sharpeRatio || null
        this.metricData.maxDrawdown = data.maxDrawdown || null
      }).catch(err => {
        console.error('MetricFormulas 请求失败：', err)
        this.$message.error('获取指标失败')
      })
    },

    renderChart (prices) {
      const dates = prices.map(item => item.date)
      const values = prices.map(item => item.close ?? item.price ?? item.value)

      // 计算指标
      this.metrics.max = Math.max(...values)
      this.metrics.min = Math.min(...values)
      const firstVal = values[0]
      const lastVal = values[values.length - 1]
      this.metrics.changePercent = firstVal !== 0 ? ((lastVal - firstVal) / firstVal) * 100 : 0

      if (this.chart) {
        this.chart.dispose()
      }
      this.chart = echarts.init(this.$refs.chartRef)
      const isUp = this.metrics.changePercent >= 0
      const lineColor = isUp ? '#ef4444' : '#10b981'
      const areaColorTop = isUp ? 'rgba(239,68,68,0.25)' : 'rgba(16,185,129,0.25)'
      const areaColorBottom = isUp ? 'rgba(239,68,68,0.02)' : 'rgba(16,185,129,0.02)'

      const option = {
        grid: {
          left: '3%',
          right: '4%',
          top: '20%',
          bottom: '8%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: dates,
          boundaryGap: false,
          axisLine: { lineStyle: { color: '#cbd5e1' } },
          axisTick: { show: false },
          axisLabel: {
            color: '#94a3b8',
            fontSize: 11,
            interval: Math.max(1, Math.floor(dates.length / 16)),
            rotate: dates.length > 20 ? 45 : 0,
            showMaxLabel: true,
            showMinLabel: true
          }
        },
        yAxis: {
          type: 'value',
          scale: true,
          splitLine: { lineStyle: { color: '#f1f5f9' } },
          axisLabel: {
            color: '#94a3b8',
            fontSize: 11,
            formatter: (val) => val.toFixed(2)
          }
        },
        tooltip: {
          trigger: 'axis',
          backgroundColor: '#fff',
          borderColor: '#e2e8f0',
          textStyle: { color: '#334155', fontSize: 12 },
          formatter: (params) => {
            const item = params[0]
            return `<div style="font-size:13px;color:#475569;margin-bottom:6px;">${item.axisValue}</div>
                      <div style="font-weight:700;color:#0f172a;font-size:16px;">¥${Number(item.value).toFixed(2)}</div>`
          }
        },
        series: [{
          type: 'line',
          data: values,
          smooth: true,
          symbol: 'none',
          lineStyle: {
            color: lineColor,
            width: 2
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: areaColorTop },
              { offset: 1, color: areaColorBottom }
            ])
          },
          markPoint: {
            symbol: 'pin',
            symbolSize: 50,
            data: [
              { type: 'max', name: '最高', itemStyle: { color: '#ef4444' } },
              { type: 'min', name: '最低', itemStyle: { color: '#10b981' } }
            ],
            label: {
              fontSize: 12,
              fontWeight: 'bold',
              color: '#1e293b',
              offset: [0, -10],
              formatter: (p) => '¥' + Number(p.value).toFixed(2)
            }
          }
        }]
      }

      this.chart.setOption(option)
    },

    formatPrice (val) {
      return val ? Number(val).toFixed(2) : '-'
    },

    handleResize () {
      if (this.chart) {
        this.chart.resize()
      }
    },

    switchDateRange (range) {
      this.dateRange = range
      localStorage.setItem('jiaoyishuju_dateRange', range)
      if (range === 'year') {
        this.endDate = this.originalEndDate
        const today = new Date()
        const yearAgo = new Date(today)
        yearAgo.setFullYear(yearAgo.getFullYear() - 1)
        const yearAgoStr = yearAgo.toISOString().split('T')[0]
        this.startDate = this.originalStartDate > yearAgoStr ? this.originalStartDate : yearAgoStr
      } else {
        this.startDate = this.originalStartDate
        this.endDate = this.originalEndDate
      }
      this.loadData()
      this.loadMetrics()
    },

    switchMetric (metric) {
      this.activeMetric = metric
      localStorage.setItem('jiaoyishuju_activeMetric', metric)
    },

    handleStorageSync (e) {
      if (e.key === 'jiaoyishuju_activeMetric' && e.newValue) {
        this.activeMetric = e.newValue
      } else if (e.key === 'jiaoyishuju_dateRange' && e.newValue) {
        if (this.dateRange !== e.newValue) {
          this.switchDateRange(e.newValue)
        }
      }
    },

    handleBack () {
      this.$router.back()
    }
  }
}
</script>

<style scoped lang="less">
.jiaoyishuju-page {
  height: 100vh;
  background: url('../../assets/bg-hp.png') no-repeat;
  background-size: 100% 100%;
  display: flex;
  flex-direction: column;
}

.page-topbar {
  display: flex;
  align-items: center;
  padding: 8px 24px;
  background: white;
  border-bottom: 1px solid #e8eef7;
  position: relative;
  flex-shrink: 0;
}

.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: none;
  background: transparent;
  color: #3b82f6;
  font-size: 14px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  transition: background 0.2s;
}

.back-btn:hover {
  background: #eff6ff;
}

.topbar-title {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
}

.page-content {
  flex: 1;
  overflow-y: auto;
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  padding: 10px 32px 12px;
}

.state-view {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 60px 0;
  color: #94a3b8;
  font-size: 15px;
}

.product-header {
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 2px;
}

.product-name {
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
}

.product-code {
  font-size: 14px;
  color: #64748b;
}

.date-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.date-range {
  font-size: 13px;
  color: #94a3b8;
}

.date-switch {
  display: flex;
  gap: 4px;
  background: #f1f5f9;
  border-radius: 8px;
  padding: 4px;
}

.switch-btn {
  padding: 6px 16px;
  font-size: 13px;
  border: none;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  color: #64748b;
  transition: all 0.2s;
}

.switch-btn.active {
  background: white;
  color: #3b82f6;
  font-weight: 600;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.chart-container {
  background: white;
  border-radius: 12px;
  padding: 10px 12px 6px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  margin-bottom: 8px;
}

.price-chart {
  width: 100%;
  height: 260px;
}

.metrics-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-bottom: 8px;
}

.metric-card {
  background: white;
  border-radius: 10px;
  padding: 10px 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  text-align: center;
}

.metric-label {
  font-size: 12px;
  color: #94a3b8;
  margin-bottom: 2px;
}

.metric-value {
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
}

.metric-value.red {
  color: #ef4444;
}

.metric-value.green {
  color: #10b981;
}

.metrics-formula-section {
  background: white;
  border-radius: 12px;
  padding: 10px 14px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.section-title {
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;
  flex-shrink: 0;
}

.metric-tabs {
  display: flex;
  gap: 4px;
}

.metric-tab {
  padding: 5px 12px;
  font-size: 12px;
  border: 1px solid #e2e8f0;
  background: white;
  border-radius: 6px;
  cursor: pointer;
  color: #64748b;
  transition: all 0.2s;
}

.metric-tab.active {
  background: #3b82f6;
  color: white;
  border-color: #3b82f6;
}

.metric-content-row {
  display: flex;
  gap: 12px;
  border-top: 1px solid #f1f5f9;
  padding-top: 8px;
}

.metric-formula-left {
  flex: 0 0 40%;
  max-width: 40%;
  background: #f8fafc;
  border-radius: 10px;
  padding: 10px;
}

.formula-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
}

.formula-icon {
  width: 24px;
  height: 24px;
  background: #eff6ff;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #3b82f6;
}

.formula-title {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
}

.formula-box {
  margin-bottom: 6px;
}

.formula-label {
  font-size: 11px;
  color: #94a3b8;
  display: block;
  margin-bottom: 2px;
}

.formula-text {
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: 11px;
  color: #1e293b;
  background: white;
  padding: 6px 8px;
  border-radius: 6px;
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: break-word;
}

.formula-desc {
  font-size: 11px;
  color: #64748b;
  line-height: 1.4;
}

.metric-calc-right {
  flex: 0 0 60%;
  max-width: 60%;
}

.calc-header {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 6px;
}

.calc-steps {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px;
  background: #f8fafc;
  border-radius: 10px;
}

.calc-step {
  display: flex;
  gap: 8px;
  align-items: flex-start;
}

.step-number {
  width: 18px;
  height: 18px;
  background: #3b82f6;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 600;
  flex-shrink: 0;
}

.step-text {
  font-size: 11px;
  color: #475569;
  line-height: 1.4;
}

/* 竖屏适配 */
@media screen and (orientation: portrait) {
  .jiaoyishuju-page {
    background: url('../../assets/bg-sp.png') no-repeat;
    background-size: 100% 100%;
  }

  .page-topbar {
    padding: 12px 16px;
  }

  .page-content {
    padding: 14px 14px 18px;
  }

  .price-chart {
    height: 200px;
  }

  .metrics-row {
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    margin-bottom: 10px;
  }

  .metric-card {
    padding: 10px 8px;
  }

  .metric-value {
    font-size: 16px;
  }

  .date-section {
    flex-direction: column;
    gap: 8px;
    align-items: flex-start;
  }

  .metric-tabs {
    flex-wrap: wrap;
    gap: 4px;
  }

  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .metric-tab {
    padding: 5px 10px;
    font-size: 12px;
  }

  .metric-content-row {
    flex-direction: column;
    gap: 10px;
  }

  .metric-formula-left {
    flex: 1 1 auto;
    max-width: 100%;
  }

  .metric-calc-right {
    flex: 1 1 auto;
    max-width: 100%;
  }

  .calc-steps {
    gap: 6px;
  }
}

@media (max-width: 600px) {
  .price-chart {
    height: 180px;
  }

  .metrics-row {
    grid-template-columns: 1fr;
  }
}
</style>
